import platformConfigJson from '../../platform.config.json'

/**
 * White-Label Branding Configuration
 *
 * Priority order:
 * 1. Environment variables (NEXT_PUBLIC_*)
 * 2. platform.config.json values
 *
 * To customize for a new project:
 * 1. Edit platform.config.json → branding.colors  (hex, for brand utilities)
 * 2. Edit platform.config.json → branding.theme   (HSL, for shadcn/ui tokens)
 *
 * Both systems are applied at runtime by BrandingProvider, making ALL
 * components (shadcn primitives + custom brand utilities) follow the config.
 *
 * @example
 * ```ts
 * import { brandingConfig } from '@/config/branding'
 *
 * <h1>{brandingConfig.appName}</h1>
 * <div style={{ color: brandingConfig.colors.primary }}>Branded</div>
 * ```
 */

// ─── Types ──────────────────────────────────────

/** Hex colors used for brand CSS utility classes (bg-brand-*, text-brand-*) */
export interface BrandingColors {
	primary: string
	secondary: string
	accent: string
	background: string
	foreground: string
	surface: string
	border: string
	muted: string
	destructive: string
}

/**
 * HSL values for shadcn/ui CSS custom properties.
 * Format: "H S% L%" (no hsl() wrapper) — matches shadcn convention.
 * These drive ALL shadcn primitives: Button, Input, Card, Dialog, etc.
 */
export interface ThemeColors {
	background: string
	foreground: string
	card: string
	cardForeground: string
	popover: string
	popoverForeground: string
	primary: string
	primaryForeground: string
	secondary: string
	secondaryForeground: string
	muted: string
	mutedForeground: string
	accent: string
	accentForeground: string
	destructive: string
	destructiveForeground: string
	border: string
	input: string
	ring: string
	radius: string
	chart1: string
	chart2: string
	chart3: string
	chart4: string
	chart5: string
	sidebarBackground: string
	sidebarForeground: string
	sidebarPrimary: string
	sidebarPrimaryForeground: string
	sidebarAccent: string
	sidebarAccentForeground: string
	sidebarBorder: string
	sidebarRing: string
}

export interface BrandingConfig {
	appName: string
	shortName: string
	description: string
	tagline: string
	/** Hex colors — drive brand-* CSS utilities */
	colors: BrandingColors
	/** HSL theme — drive shadcn/ui CSS custom properties */
	theme: ThemeColors
	logoUrl: string
	favicon: string
	fontPrimary: string
	fontFallback: string
}

// ─── Helper ─────────────────────────────────────

function resolve(envVar: string | undefined, fallback: string): string {
	return envVar ?? fallback
}

// ─── Configuration ──────────────────────────────

const json = platformConfigJson

export const brandingConfig: BrandingConfig = {
	appName: resolve(process.env.NEXT_PUBLIC_APP_NAME, json.branding.appName),
	shortName: resolve(process.env.NEXT_PUBLIC_SHORT_NAME, json.branding.shortName),
	description: resolve(process.env.NEXT_PUBLIC_DESCRIPTION, json.branding.description),
	tagline: resolve(process.env.NEXT_PUBLIC_TAGLINE, json.branding.tagline),
	colors: {
		primary: resolve(process.env.NEXT_PUBLIC_PRIMARY_COLOR, json.branding.colors.primary),
		secondary: resolve(process.env.NEXT_PUBLIC_SECONDARY_COLOR, json.branding.colors.secondary),
		accent: resolve(process.env.NEXT_PUBLIC_ACCENT_COLOR, json.branding.colors.accent),
		background: resolve(process.env.NEXT_PUBLIC_BACKGROUND_COLOR, json.branding.colors.background),
		foreground: resolve(process.env.NEXT_PUBLIC_FOREGROUND_COLOR, json.branding.colors.foreground),
		surface: resolve(process.env.NEXT_PUBLIC_SURFACE_COLOR, json.branding.colors.surface),
		border: resolve(process.env.NEXT_PUBLIC_BORDER_COLOR, json.branding.colors.border),
		muted: resolve(process.env.NEXT_PUBLIC_MUTED_COLOR, json.branding.colors.muted),
		destructive: resolve(
			process.env.NEXT_PUBLIC_DESTRUCTIVE_COLOR,
			json.branding.colors.destructive
		),
	},
	theme: {
		background: json.branding.theme.background,
		foreground: json.branding.theme.foreground,
		card: json.branding.theme.card,
		cardForeground: json.branding.theme.cardForeground,
		popover: json.branding.theme.popover,
		popoverForeground: json.branding.theme.popoverForeground,
		primary: json.branding.theme.primary,
		primaryForeground: json.branding.theme.primaryForeground,
		secondary: json.branding.theme.secondary,
		secondaryForeground: json.branding.theme.secondaryForeground,
		muted: json.branding.theme.muted,
		mutedForeground: json.branding.theme.mutedForeground,
		accent: json.branding.theme.accent,
		accentForeground: json.branding.theme.accentForeground,
		destructive: json.branding.theme.destructive,
		destructiveForeground: json.branding.theme.destructiveForeground,
		border: json.branding.theme.border,
		input: json.branding.theme.input,
		ring: json.branding.theme.ring,
		radius: json.branding.theme.radius,
		chart1: json.branding.theme.chart1,
		chart2: json.branding.theme.chart2,
		chart3: json.branding.theme.chart3,
		chart4: json.branding.theme.chart4,
		chart5: json.branding.theme.chart5,
		sidebarBackground: json.branding.theme.sidebarBackground,
		sidebarForeground: json.branding.theme.sidebarForeground,
		sidebarPrimary: json.branding.theme.sidebarPrimary,
		sidebarPrimaryForeground: json.branding.theme.sidebarPrimaryForeground,
		sidebarAccent: json.branding.theme.sidebarAccent,
		sidebarAccentForeground: json.branding.theme.sidebarAccentForeground,
		sidebarBorder: json.branding.theme.sidebarBorder,
		sidebarRing: json.branding.theme.sidebarRing,
	},
	logoUrl: resolve(process.env.NEXT_PUBLIC_LOGO_URL, json.branding.logo.url),
	favicon: resolve(process.env.NEXT_PUBLIC_FAVICON, json.branding.logo.favicon),
	fontPrimary: resolve(process.env.NEXT_PUBLIC_FONT_PRIMARY, json.branding.fonts.primary),
	fontFallback: resolve(process.env.NEXT_PUBLIC_FONT_FALLBACK, json.branding.fonts.fallback),
}

// ─── CSS Variables ──────────────────────────────

/**
 * Generate ALL CSS custom properties for runtime theming.
 * Applied once by BrandingProvider to document.documentElement.
 *
 * This bridges the two token systems:
 *  - --brand-*  → hex values for bg-brand-*, text-brand-* Tailwind utilities
 *  - shadcn HSL → drives ALL shadcn/ui primitives (Button, Input, Card, etc.)
 *
 * Result: changing platform.config.json controls the ENTIRE visual system.
 */
export function getCSSVariables(): Record<string, string> {
	const { colors, theme } = brandingConfig
	return {
		// ── Brand hex utilities ──────────────────────
		'--brand-primary': colors.primary,
		'--brand-secondary': colors.secondary,
		'--brand-accent': colors.accent,
		'--brand-background': colors.background,
		'--brand-foreground': colors.foreground,
		'--brand-surface': colors.surface,
		'--brand-border': colors.border,
		'--brand-muted': colors.muted,
		'--brand-destructive': colors.destructive,

		// ── shadcn/ui HSL tokens ──────────────────────
		'--background': theme.background,
		'--foreground': theme.foreground,
		'--card': theme.card,
		'--card-foreground': theme.cardForeground,
		'--popover': theme.popover,
		'--popover-foreground': theme.popoverForeground,
		'--primary': theme.primary,
		'--primary-foreground': theme.primaryForeground,
		'--secondary': theme.secondary,
		'--secondary-foreground': theme.secondaryForeground,
		'--muted': theme.muted,
		'--muted-foreground': theme.mutedForeground,
		'--accent': theme.accent,
		'--accent-foreground': theme.accentForeground,
		'--destructive': theme.destructive,
		'--destructive-foreground': theme.destructiveForeground,
		'--border': theme.border,
		'--input': theme.input,
		'--ring': theme.ring,
		'--radius': theme.radius,
		'--chart-1': theme.chart1,
		'--chart-2': theme.chart2,
		'--chart-3': theme.chart3,
		'--chart-4': theme.chart4,
		'--chart-5': theme.chart5,
		'--sidebar-background': theme.sidebarBackground,
		'--sidebar-foreground': theme.sidebarForeground,
		'--sidebar-primary': theme.sidebarPrimary,
		'--sidebar-primary-foreground': theme.sidebarPrimaryForeground,
		'--sidebar-accent': theme.sidebarAccent,
		'--sidebar-accent-foreground': theme.sidebarAccentForeground,
		'--sidebar-border': theme.sidebarBorder,
		'--sidebar-ring': theme.sidebarRing,

		// ── Font ──────────────────────────────────────
		'--font-primary': brandingConfig.fontPrimary,
		'--font-fallback': brandingConfig.fontFallback,
	}
}

// ─── Convenience Accessors ──────────────────────

export const getAppName = (): string => brandingConfig.appName
export const getPrimaryColor = (): string => brandingConfig.colors.primary
